// Speech verb — text → sample.
//
// speechSynthesis.speak() plays audibly but does NOT expose its output
// to Web Audio. The realistic capture path in the browser is
// getDisplayMedia({ audio: true }): the user shares this tab's audio
// (or the whole system's), and we record that stream while the TTS
// engine speaks.
//
// Caveats that are honestly surfaced in the UI:
//   - getDisplayMedia with tab-audio requires explicit user consent in a
//     picker. On macOS, system-audio capture is gated further.
//   - Not all browsers expose audio in getDisplayMedia. Firefox is
//     video-only as of 2026.
//   - Preview plays audibly without capture.
// If capture is unsupported, the user still gets TTS preview; they can
// fall back to phone-recording the output through the Record verb.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { VerbProps } from '../types';

type Status =
  | { kind: 'idle' }
  | { kind: 'speaking' }
  | { kind: 'capturing' }
  | { kind: 'decoding' }
  | { kind: 'error'; detail: string };

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) if (MediaRecorder.isTypeSupported(m)) return m;
  return null;
}

export function SpeechVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const [text, setText] = useState('hello');
  const [voiceURI, setVoiceURI] = useState<string>('');
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Populate voices. Browsers load them async; we listen for
  // voiceschanged as well as reading synchronously.
  useEffect(() => {
    if (typeof speechSynthesis === 'undefined') return;
    const refresh = () => {
      const list = speechSynthesis.getVoices();
      setVoices(list);
      if (!voiceURI && list.length > 0) setVoiceURI(list[0]!.voiceURI);
    };
    refresh();
    speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => speechSynthesis.removeEventListener('voiceschanged', refresh);
  }, [voiceURI]);

  const makeUtterance = useCallback((): SpeechSynthesisUtterance | null => {
    if (typeof SpeechSynthesisUtterance === 'undefined') return null;
    const u = new SpeechSynthesisUtterance(text.slice(0, 200));
    u.rate = rate;
    u.pitch = pitch;
    const voice = voices.find((v) => v.voiceURI === voiceURI);
    if (voice) u.voice = voice;
    return u;
  }, [text, rate, pitch, voices, voiceURI]);

  const preview = useCallback(() => {
    if (typeof speechSynthesis === 'undefined') {
      setStatus({ kind: 'error', detail: 'speechSynthesis not supported' });
      return;
    }
    const u = makeUtterance();
    if (!u) return;
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
    setStatus({ kind: 'speaking' });
    u.onend = () => setStatus({ kind: 'idle' });
    u.onerror = () => setStatus({ kind: 'error', detail: 'TTS error' });
  }, [makeUtterance]);

  const teardown = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // already stopping
      }
    }
    recorderRef.current = null;
    const s = streamRef.current;
    if (s) for (const t of s.getTracks()) t.stop();
    streamRef.current = null;
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const captureAndHandoff = useCallback(async () => {
    if (!audioContext) {
      setStatus({ kind: 'error', detail: 'engine not booted' });
      return;
    }
    if (typeof speechSynthesis === 'undefined') {
      setStatus({ kind: 'error', detail: 'speechSynthesis not supported' });
      return;
    }
    const md = (
      navigator.mediaDevices as unknown as {
        getDisplayMedia?: (c: { video: boolean; audio: boolean }) => Promise<MediaStream>;
      }
    ).getDisplayMedia;
    if (typeof md !== 'function') {
      setStatus({
        kind: 'error',
        detail: 'getDisplayMedia not supported — try Chrome or record via mic instead',
      });
      return;
    }
    const mime = pickMime();
    if (!mime) {
      setStatus({ kind: 'error', detail: 'no supported MediaRecorder mime' });
      return;
    }

    let stream: MediaStream;
    try {
      stream = await md.call(navigator.mediaDevices, { video: true, audio: true });
    } catch (e) {
      setStatus({
        kind: 'error',
        detail: 'capture cancelled: ' + (e instanceof Error ? e.message : String(e)),
      });
      return;
    }
    // Browsers require video:true to offer tab-audio; we only want the
    // audio track for the buffer. Stop video tracks immediately.
    for (const t of stream.getVideoTracks()) t.stop();
    if (stream.getAudioTracks().length === 0) {
      for (const t of stream.getTracks()) t.stop();
      setStatus({
        kind: 'error',
        detail: 'no audio track granted — select "Share tab audio" in the picker',
      });
      return;
    }
    streamRef.current = stream;

    const u = makeUtterance();
    if (!u) {
      teardown();
      return;
    }
    const audioStream = new MediaStream(stream.getAudioTracks());
    const rec = new MediaRecorder(audioStream, { mimeType: mime });
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.start(250);
    setStatus({ kind: 'capturing' });

    u.onend = () => void finishCapture();
    u.onerror = () => {
      setStatus({ kind: 'error', detail: 'TTS error during capture' });
      teardown();
    };
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  }, [audioContext, makeUtterance, teardown]);

  const finishCapture = useCallback(async () => {
    const rec = recorderRef.current;
    const ctx = audioContext;
    if (!rec || !ctx) return;
    setStatus({ kind: 'decoding' });
    await new Promise<void>((resolve) => {
      rec.addEventListener('stop', () => resolve(), { once: true });
      try {
        rec.stop();
      } catch {
        resolve();
      }
    });
    const blob = new Blob(chunksRef.current, { type: rec.mimeType });
    chunksRef.current = [];
    recorderRef.current = null;
    const stream = streamRef.current;
    if (stream) for (const t of stream.getTracks()) t.stop();
    streamRef.current = null;
    try {
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      const voice = voices.find((v) => v.voiceURI === voiceURI);
      const suggestedName = 'speech_' + text.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 20);
      onBuffer(buffer, {
        suggestedName,
        origin: {
          kind: 'speech',
          text,
          voice: voice?.name ?? 'default',
        },
      });
      onStatus(`Captured ${buffer.duration.toFixed(1)}s of speech — trim and save.`, 'ok');
      setStatus({ kind: 'idle' });
    } catch (e) {
      setStatus({
        kind: 'error',
        detail: 'decode failed: ' + (e instanceof Error ? e.message : String(e)),
      });
    }
  }, [audioContext, onBuffer, onStatus, text, voiceURI, voices]);

  return (
    <div className="flex flex-col gap-4 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Type text, pick a voice, Preview to hear it; Capture to record via tab-audio sharing.
      </div>

      <label className="flex flex-col gap-1">
        <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Text (≤ 200 chars)</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 200))}
          rows={2}
          className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-sm text-zinc-100 resize-none focus:border-yellow-300 focus:outline-none"
        />
      </label>

      <div className="grid grid-cols-1 gap-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Voice</span>
          <select
            value={voiceURI}
            onChange={(e) => setVoiceURI(e.target.value)}
            className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-zinc-200"
            disabled={voices.length === 0}
          >
            {voices.length === 0 ? (
              <option value="">(no voices available)</option>
            ) : (
              voices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))
            )}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-20">Rate</span>
          <input
            type="range"
            min={0.3}
            max={3}
            step={0.1}
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            className="accent-yellow-300 flex-1"
          />
          <span className="tabular-nums w-12 text-right text-zinc-300">{rate.toFixed(1)}×</span>
        </label>
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-20">Pitch</span>
          <input
            type="range"
            min={0.1}
            max={2}
            step={0.1}
            value={pitch}
            onChange={(e) => setPitch(Number(e.target.value))}
            className="accent-yellow-300 flex-1"
          />
          <span className="tabular-nums w-12 text-right text-zinc-300">{pitch.toFixed(1)}×</span>
        </label>
      </div>

      {status.kind === 'error' && (
        <div className="bg-rose-950/40 border border-rose-800 px-4 py-2 text-xs text-rose-200">
          {status.detail}
        </div>
      )}
      {(status.kind === 'speaking' || status.kind === 'capturing' || status.kind === 'decoding') && (
        <div className="bg-zinc-900 border border-zinc-800 px-4 py-2 text-xs text-yellow-200">
          {status.kind === 'speaking' && 'Speaking…'}
          {status.kind === 'capturing' && 'Capturing tab audio — utterance in progress…'}
          {status.kind === 'decoding' && 'Decoding take…'}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={preview}
          disabled={status.kind !== 'idle' && status.kind !== 'error'}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 disabled:opacity-30"
        >
          ▶ Preview
        </button>
        <button
          type="button"
          onClick={() => void captureAndHandoff()}
          disabled={status.kind !== 'idle' && status.kind !== 'error'}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          ● Capture (share tab audio)
        </button>
      </div>
    </div>
  );
}
